import { ActionButton, ConfirmationAlert } from '@emerald-works-nova/components'
import {
  Grid,
  Box
} from '@mui/material'
import React, { useState } from 'react'
import { useTenantFormContext } from '../../../../tenant-form-context'
import { useStyles } from './style'
import SectionsSelector from '../../page-builder/sections-selector'
import SectionEditor from '../../page-builder/section-editor'

import EditablePageHeader from './editable-page-header'
import EditPageNameModal from './edit-page-name-modal'

const PageConfiguration = ({ page, onBack, onPageUpdate, isSaving }) => {
  const classes = useStyles()

  // Modal state management
  const [editModalOpen, setEditModalOpen] = useState(false)

  // Unsaved changes modal state
  // Shows confirmation when user tries to navigate away with unsaved changes
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false)

  // Tracks if user has made changes to the page
  // Used to show/hide "Discard Changes" button and trigger unsaved changes modal
  const [hasChanges, setHasChanges] = useState(false)

  const { mainClasses, isEdit } = useTenantFormContext()

  // Use page sections or default sections for new pages
  // This state is separate from the original page to track changes
  const [sections, setSections] = useState(page.sections || [])
  const [selectedSection, setSelectedSection] = useState(sections[0] || null)

  const handleSectionSelect = (section) => {
    setSelectedSection(section)
  }

  const handleSectionChange = (sectionOrder, updatedSection) => {
    setSections(prev => prev.map(section =>
      section.order === sectionOrder ? updatedSection : section
    ))
    setSelectedSection(updatedSection)
    setHasChanges(true) // Mark that changes have been made
  }

  const handleAddSection = (sectionType) => {
    // Default data templates for each section type
    const defaultSectionData = {
      mtBanner: {
        data: {
          header: '',
          subHeader: '',
          description: '',
          cta: '',
          image: {}
        }
      },
      featuresBanner: {
        data: {
          header: '',
          subHeader: '',
          description: '',
          cards: [
            { order: 0, title: '', description: '', cta: '' }
          ]
        }
      },
      contentExplorer: {
        data: {
          header: ''
        }
      },
      featureCard: {
        data: {
          cards: [
            { order: 0, header: '', subHeader: '', description: '', cta: '' }
          ]
        }
      }
    }

    const newSection = {
      type: sectionType,
      enabled: true,
      order: sections.length, // Set order based on current sections length (0-based)
      ...defaultSectionData[sectionType]
    }

    setSections(prev => [...prev, newSection])
    setSelectedSection(newSection)
    setHasChanges(true) // Mark that changes have been made
  }

  const handleSectionDelete = (sectionOrder) => {
    setSections(prev => {
      // Filter out the deleted section
      const remainingSections = prev.filter(section => section.order !== sectionOrder)

      // Update the order property of each remaining section to maintain sequential order
      const sectionsWithUpdatedOrder = remainingSections.map((section, index) => ({
        ...section,
        order: index // order is 0-based (0, 1, 2, ...)
      }))

      return sectionsWithUpdatedOrder
    })

    // Calculate remaining sections for selected section update
    const remainingSections = sections.filter(section => section.order !== sectionOrder)

    // Update selected section if the deleted section was selected
    if (remainingSections.length > 0) {
      // Select the first remaining section with updated order
      setSelectedSection({ ...remainingSections[0], order: 0 })
    } else {
      setSelectedSection(null)
    }
    setHasChanges(true) // Mark that changes have been made
  }

  const handleReorderSections = (fromIndex, toIndex) => {
    setSections(prev => {
      const newSections = [...prev]
      const [movedSection] = newSections.splice(fromIndex, 1)
      newSections.splice(toIndex, 0, movedSection)

      // Update the order property of each section to match its new position
      const sectionsWithUpdatedOrder = newSections.map((section, index) => ({
        ...section,
        order: index // order is 0-based
      }))

      // Update selectedSection to have the correct order after reordering
      if (selectedSection) {
        const updatedSelectedSection = sectionsWithUpdatedOrder.find(section =>
          section.type === selectedSection.type &&
          section.instanceId === selectedSection.instanceId
        )
        if (updatedSelectedSection) {
          setSelectedSection(updatedSelectedSection)
        }
      }

      return sectionsWithUpdatedOrder
    })
    setHasChanges(true) // Mark that changes have been made
  }

  const handlePageNameEdit = () => {
    setEditModalOpen(true)
  }

  const handlePageNameSave = (updatedPage) => {
    onPageUpdate(updatedPage)
  }

  const handleDiscardChanges = () => {
    // Reset sections to original page sections
    setSections(page.sections || [])
    setSelectedSection(page.sections?.[0] || null)
    setHasChanges(false)
  }

  const handleBackWithChanges = () => {
    if (hasChanges) {
      setUnsavedChangesModalOpen(true)
    } else {
      onBack()
    }
  }

  const handleDiscardAndBack = () => {
    handleDiscardChanges()
    onBack()
  }

  const handleSave = async (status = page.status) => {
    onPageUpdate(sections)
    setHasChanges(false)

    if (status === 'published' && page.status === 'published') {
      onBack()
    }
  }

  return (
    <Grid container>
      {/* Header with EditablePageHeader and Action Buttons */}
      <Grid container alignItems='center' className={mainClasses.ctnTitle}>
        <Grid item xs={8}>
          {/* Editable page header with breadcrumb and page name */}
          <EditablePageHeader
            page={page}
            onPageNameEdit={handlePageNameEdit}
            isEdit={isEdit}
            onBack={handleBackWithChanges}
          />
        </Grid>

        {/* Action Buttons - Different sets based on page status and edit mode */}
        <Grid container item justifyContent='flex-end' xs={4} spacing={2}>
          {isEdit && (
            <>
              {/* Draft Page Actions */}
              {!page.isPublished ? (
                <>
                  <Grid item>
                    <ActionButton
                      variant='secondary'
                      dataTest='cms-save-draft-button'
                      onClick={() => handleSave('draft')}
                      isWorking={isSaving}
                    >
                      Save as Draft
                    </ActionButton>
                  </Grid>
                  <Grid item>
                    <ActionButton
                      variant='primary'
                      dataTest='cms-publish-page-button'
                      onClick={() => handleSave('published')}
                      isWorking={isSaving}
                    >
                      Publish Page
                    </ActionButton>
                  </Grid>
                </>
              ) : (
                /* Published Page Actions */
                <>
                  {/* Discard Changes - Only for default pages with changes */}
                  {page.isDefault && hasChanges && (
                    <Grid item>
                      <ActionButton
                        variant='secondary'
                        dataTest='cms-discard-changes-button'
                        onClick={handleDiscardChanges}
                      >
                        Discard Changes
                      </ActionButton>
                    </Grid>
                  )}

                  {/* Unpublish - Only for non-default pages */}
                  {!page.isDefault && (
                    <Grid item>
                      <ActionButton
                        variant='secondary'
                        dataTest='cms-unpublish-button'
                        onClick={() => handleSave('draft')}
                        isWorking={isSaving}
                      >
                        Unpublish
                      </ActionButton>
                    </Grid>
                  )}

                  {/* Update Published - Always available for published pages */}
                  <Grid item>
                    <ActionButton
                      variant='primary'
                      dataTest='cms-update-published-button'
                      onClick={() => handleSave('published')}
                      isWorking={isSaving}
                    >
                      Update Published
                    </ActionButton>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {/* Main Content Area - Two Panel Layout */}
      <Grid item xs={12} className={classes.contentContainer}>
        <Box className={classes.mainContainer}>
          {/* Left Panel - Sections Selector */}
          <Box className={classes.leftPanel}>
            <SectionsSelector
              sections={sections}
              selectedSection={selectedSection}
              onSectionSelect={handleSectionSelect}
              onAddSection={handleAddSection}
              onReorderSections={handleReorderSections}
              isEdit={isEdit}
            />
          </Box>

          {/* Right Panel - Section Editor */}
          <Box className={classes.rightPanel}>
            <SectionEditor
              section={selectedSection}
              onSectionChange={handleSectionChange}
              onSectionDelete={handleSectionDelete}
              isEdit={isEdit}
            />
          </Box>
        </Box>
      </Grid>

      {/* Page Name Edit Modal */}
      <EditPageNameModal
        open={editModalOpen}
        page={page}
        onClose={() => setEditModalOpen(false)}
        onSave={handlePageNameSave}
      />

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationAlert
        title='You have unsaved changes on this page.'
        description='Your changes will be lost if you discard them. This action cannot be undone.'
        agreeText='Discard Changes'
        showModal={unsavedChangesModalOpen}
        onConfirmation={handleDiscardAndBack}
        onCancel={() => setUnsavedChangesModalOpen(false)}
        disableBackdropClick
      />
    </Grid>
  )
}

export default PageConfiguration
